import { Color } from '@tiptap/extension-color'
import {TextStyle} from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'
import TextAlign from '@tiptap/extension-text-align' //https://tiptap.dev/docs/examples/basics/formatting
import { EditorProvider, useCurrentEditor, Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import EffectThemeButton from "../../effectTheme/effectThemeButtons";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FontFamily } from '../../../utils/FontFamily'
import { FontSize } from '../../../utils/FontSize';
import Image from '@tiptap/extension-image'//https://tiptap.dev/docs/examples/basics/images
import {Table} from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'//https://tiptap.dev/docs/examples/basics/tables
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import TaskItem from '@tiptap/extension-task-item'//https://tiptap.dev/docs/examples/basics/tasks
import TaskList from '@tiptap/extension-task-list'
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { faBold,faItalic,faLightbulb,faStrikethrough,faUnderline,faHeader,faListOl,faListUl,faUndo,faParagraph,faTable,faSubscript,faFont,faSuperscript, faAlignLeft, faAlignCenter, faAlignRight, faCode,faPaintBrush,faBan,faMinusCircle, faChainBroken, faMinus, faRotateRight, faDownload, faCaretDown, faImage, faCheckSquare, faTextHeight } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useRef, useState } from 'react'
import { saveAs } from 'file-saver';
import { v4 as uuidv4 } from 'uuid';
import { SengiColors } from '../../../utils/SengiColors'
import { useTranslation } from 'react-i18next'
import { useBootstrapTheme } from '../../../contexts/BootstrapThemeContext'
import EffectThemeInput from '../../effectTheme/effectThemeInput'

interface MenuBarProps {
  editorIN: Editor | null;
};

//https://tiptap.dev/docs/examples/basics/default-text-editor
export const MenuBarRichTextEditor= ({editorIN}: MenuBarProps) => {
  const {t} = useTranslation('toolsApps');
  const { effectiveTheme } = useBootstrapTheme();
  //DEFINITIONS
  const editorContext = useCurrentEditor();
  const editor: Editor | null = editorIN ?? editorContext.editor;
  const buttonHeight = '42px';
  const buttonWidth = '50px';

  const [fontSize, setFontSize] = useState(12);
  const [fontColor,setFontColor] = useState('#958DF1');
  const [fileFormat, setFileFormat] = useState('');
  const fileFormatList = ['docx','html','json'];
  const fontList = ['Arial','Georgia','Courier New','Comic Sans MS',
  'Calibri','Times New Roman','Verdana','Open-Sans-Regular','Roboto-Regular','Roboto-Italic','RobotoCondensed-Regular','RobotoCondensed-Bold','RobotoCondensed-BoldItalic','RobotoCondensed-Italic','RobotoCondensed-Light','RobotoCondensed-LightItalic', ];
  const [currentFontFamily, setCurrentFontFamily] = useState('Open-Sans-Regular');
  const getCurrentFontFamily = () => currentFontFamily;

  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  //Font color
  const handleColorChange = (color: string) => {
    if(!editor){
      return;
    }
    setFontColor(color);
    editor.chain().focus().setColor(color).run();
  };
  //Font size
  const handleSetFontSize = () => {
    if (editor && fontSize) {
      editor.chain().focus().setFontSize(`${fontSize}px`).run();
    }
  };

  //Image insert
  const handleClickUploadImage = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editor) return;

  const reader = new FileReader();
    reader.onload = () => {
      if (reader.result && typeof reader.result === 'string') {
        editor.chain().focus().setImage({ src: reader.result }).run();
      }
    };
    reader.readAsDataURL(file);
  };

  //Task List items for .docx
  const convertTaskListToStatic = (html: string) => {
  return html
    .replace(
      /<li[^>]*data-checked="(true|false)"[^>]*>\s*<label[^>]*>[\s\S]*?<\/label>\s*<div>\s*<p>(.*?)<\/p>\s*<\/div>\s*<\/li>/g,
      (_match, checked, content) => {
        const symbol = checked === 'true' ? '☑' : '☐';
        return `<div>${symbol} ${content}</div>`;
      }
    );
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  //export data
  useEffect(() => {
    const exportData = async () => {
      if (!editor) {
        return;
      }
      const filename = `${uuidv4()}.${fileFormat}`;
      let blob: Blob;

      switch (fileFormat) {
        case 'json': {
          const json = editor?.getJSON();
          blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
          saveAs(blob, filename);
          break;
        }
        case 'html': {
          const html = editor.getHTML();
          blob = new Blob([html], { type: 'text/html' });
          saveAs(blob, filename);
          break;
        }
        case 'docx': {
          const fontFamily = getCurrentFontFamily();
          let htmlContent = convertTaskListToStatic(editor.getHTML());
          
          const html = `
            <html xmlns:o='urn:schemas-microsoft-com:office:office'
                  xmlns:w='urn:schemas-microsoft-com:office:word'
                  xmlns='http://www.w3.org/TR/REC-html40'>
              <head>
                <meta charset='utf-8'>
                <title>Export</title>
                <style>
                  body, p, span, div {
                    font-family: ${fontFamily} !important;
                  }
                  h1, h2, h3, h4, h5, h6 {
                    font-family: ${fontFamily} !important;
                  }
                </style>
              </head>
              <body style="font-family: ${fontFamily} !important;">${htmlContent}</body>
            </html>
          `;
          const blob = new Blob(['\ufeff', html], {
            type: 'application/msword',
          });
          saveAs(blob, filename);
          break;
        }
      }
      setFileFormat('');
    };

    if (fileFormat && fileFormatList.includes(fileFormat)) {
      exportData();
    }
  }, [fileFormat, editor]);

  if (!editor) {
    return (<></>);
  }
  return (
    <div className="control-group flex-shrink-0 p-2 z-index-sticky mb-2 rtx-menubar">
      <div className='row mb-1'>
        <div className='col'>
          <div className="d-flex flex-wrap align-items-center gap-0">
            <EffectThemeButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleBold()
                  .run()
              }
              className=  {`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              active={editor.isActive('bold')}
              title={t('wordEditor.bold')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faBold}
                  size="1x"
                />
              }
            />
            <EffectThemeButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleItalic()
                  .run()
              }
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              active={editor.isActive('italic')}
              title={t('wordEditor.italic')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faItalic}
                  size="1x"
                />
              }
            />
            <EffectThemeButton
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleUnderline()
                  .run()
              }
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              active={editor?.isActive('underline')}
              title={t('wordEditor.underline')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faUnderline}
                  size="1x"
                />
              }
            />
            <EffectThemeButton 
              onClick={() => editor.chain().focus().toggleHighlight().run()} 
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              active={editor.isActive('highlight')}
              title={t('wordEditor.highlight')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faLightbulb}
                  size="1x"
                />
              }
            />
            <EffectThemeButton
              onClick={() => editor.chain().focus().toggleStrike().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleStrike()
                  .run()
              }
              className={`btn-outline-secondary rounded p-1 m-0 me-2 mb-1`}
              active={editor.isActive('strike')}
              title={t('wordEditor.strike')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faStrikethrough}
                  size="1x"
                />
              }
            />
            <div className="dropdown m-0 mb-1 me-1">
              <EffectThemeButton 
                className="btn-outline-secondary rounded p-2 m-0" 
                style={{height:buttonHeight, width: buttonWidth}}
                type="button" 
                id="dropdownHeader"
                data-bs-toggle="dropdown"
                title={t('wordEditor.header')}
                aria-haspopup="true" 
                children={
                  <FontAwesomeIcon
                    icon={faHeader}
                    size="lg"
                  />
                }    
              />
              <ul className={`dropdown-menu ${effectiveTheme === 'dark' ? 'dropdown-menu-dark' : 'dropdown-menu-light'} border rounded p-2`} aria-labelledby="dropdownHeader" style={{textAlignLast:'start'}}>
                <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1 ${ editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
                  H1
                </li>
                <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1 ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
                  H2
                </li>
                <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1 ${editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
                  H3
                </li>
                <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1 ${editor.isActive('heading', { level: 4 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}>
                  H4
                </li>
                <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1 ${editor.isActive('heading', { level: 5 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}>
                  H5
                </li>
                <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1 ${editor.isActive('heading', { level: 6 }) ? 'is-active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}>
                  H6
                </li>
              </ul>
            </div>

            <div className="btn-group m-0">
              <EffectThemeButton 
                className="btn-outline-secondary rounded p-2 m-0 me-1 mb-1" 
                type='button'
                data-bs-toggle="dropdown"
                title={t('wordEditor.font')}
                children={<FontAwesomeIcon icon={faFont} size="1x" />}
                style={{height:buttonHeight, width: buttonWidth}}
              />
              <ul className={`dropdown-menu ${effectiveTheme === 'dark' ? 'dropdown-menu-dark' : 'dropdown-menu-light'} p-2`}>
                {fontList.map(font => (
                  <li key={font}>
                    <button
                      className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1`}
                      style={{ fontFamily: font }}
                      onClick={() => {
                          setCurrentFontFamily(font);

                          editor
                          .chain()
                          .focus()
                          .setTextSelection({ from: 0, to: editor.state.doc.content.size })
                          .setFontFamily(font)
                          .run()
                        }
                      }
                    >
                      {font}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ position: 'relative', height: buttonHeight}} className="d-inline-flex align-items-center p-0 m-0 me-1 mb-1">
              <EffectThemeButton
                onClick={handleSetFontSize}
                className="btn-outline-secondary rounded rounded-end-0 m-0 p-0"
                title={t('wordEditor.fontSize')}
                style={{width: buttonWidth, height: buttonHeight}}
                children={
                  <FontAwesomeIcon
                    icon={faTextHeight}
                    size="1x"
                  />
                }
              />

              <EffectThemeInput
                type="number"
                min={8}
                max={96}
                value={fontSize}
                onValueChange={(value) => setFontSize(Number(value))}
                onBlur={() => {
                  editor
                    .chain()
                    .focus()
                    .setFontSize(`${fontSize}px`)
                    .run();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSetFontSize();
                    e.currentTarget.blur();
                  }
                }}
                title={t('wordEditor.fontSize')}
                inputClassName="btn-outline-secondary rounded rounded-start-0 m-0"
                style={{
                  width: buttonWidth,
                  height: buttonHeight,
                  padding: '2px 4px',
                  marginLeft: '0.5rem',
                  appearance: 'textfield',
                  outline: 'none',
                }}
              />
            </div>

            
            <EffectThemeButton
              onClick={() => editor.chain().focus().toggleSuperscript().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleSuperscript()
                  .run()
              }
              className='btn-outline-secondary rounded p-1 m-0 me-1 mb-1'
              title={t('wordEditor.superScript')}
              style={{height:buttonHeight,width: buttonWidth}}
              active={editor.isActive('superscript')}
              children={
                <FontAwesomeIcon
                  icon={faSuperscript}
                  size="1x"
                />
              }
            />
            <EffectThemeButton
              onClick={() =>editor.chain().focus().toggleSubscript().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleSubscript()
                  .run()
              }
              className='btn-outline-secondary rounded p-1 m-0 me-1 mb-1'
              title={t('wordEditor.subScript')}
              style={{height:buttonHeight,width: buttonWidth}}
              active={editor.isActive('subscript')}
              children={
                <FontAwesomeIcon
                  icon={faSubscript}
                  size="1x"
                />
              }
            />
            <div style={{ position: 'relative'}} className='me-2'>
              <EffectThemeButton
                ref={buttonRef}
                onClick={() => {
                  handleColorChange(fontColor);
                }}
                className={`d-inline-flex align-items-center btn-outline-secondary rounded p-1 m-0 me-2 mb-1`}
                active={editor.isActive('textStyle', { color: fontColor })}
                title={t('wordEditor.selectColor')}
                style={{height: buttonHeight}}
                children={
                  <>
                    <div
                      style={{
                        width: '25px',
                        height: '25px',
                        backgroundColor: fontColor,
                        border: '1px solid #ccc',
                        borderRadius: '3px',
                        display: 'inline-block',
                        verticalAlign: 'middle',
                      }}
                      className='ms-2'
                      onClick={() => setShowPicker(!showPicker)}
                      title={t('wordEditor.pickColor')}
                    />
                    <FontAwesomeIcon 
                      icon={faCaretDown} 
                      onClick={() => setShowPicker(!showPicker)}
                      title={t('wordEditor.pickColor')}
                      className='ms-1'
                    />
                  </>
                }
              />
              {showPicker && (
                <div
                  ref={pickerRef}
                  style={{
                    position: 'absolute',
                    top: buttonHeight,
                    left: 0,
                    zIndex: 1000,
                    borderRadius: '1em',
                    minWidth: '140px',
                    boxShadow: `0px 4px 10px rgba(${SengiColors.sng_black} , 0.1)`,
                  }}
                >
                  <EffectThemeInput
                    type="color"
                    value={fontColor}
                    onValueChange={(value) => handleColorChange(value)}
                    style={{ width: '65px', height: buttonHeight, cursor: 'pointer' }}
                  />
                </div>
              )}
            </div>

            
            <EffectThemeButton 
              onClick={() => editor.chain().focus().setTextAlign('left').run()} 
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              title={t('wordEditor.left')}
              active={editor.isActive({ textAlign: 'left' })}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faAlignLeft}
                  size="1x"
                />
              }
            />
            <EffectThemeButton 
              onClick={() => editor.chain().focus().setTextAlign('center').run()} 
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              active={editor.isActive({ textAlign: 'center' })}
              title={t('wordEditor.center')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faAlignCenter}
                  size="1x"
                />
              }
            />
            <EffectThemeButton 
              onClick={() => editor.chain().focus().setTextAlign('right').run()} 
              className={`btn-outline-secondary rounded p-1 m-0 me-2 mb-1`}
              active={editor.isActive({ textAlign: 'right' })}
              title={t('wordEditor.right')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faAlignRight}
                  size="1x"
                />
              }
            />
            <EffectThemeButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              active={editor.isActive('bulletList')}
              title={t('wordEditor.unorderedList')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faListUl}
                  size="1x"
                />
              }
            />
            <EffectThemeButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              active={editor.isActive('orderedList')}
              title={t('wordEditor.orderedList')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faListOl}
                  size="1x"
                />
              }
            />
            <div className="dropdown">
              <EffectThemeButton
                type="button"
                data-bs-toggle="dropdown"
                className="btn-outline-secondary rounded p-1 m-0 me-1 mb-1 dropdown-toggle"
                title={t('wordEditor.tables.name')}
                style={{ height: buttonHeight, width: buttonWidth }}
              >
                <FontAwesomeIcon icon={faTable} size="1x" />
              </EffectThemeButton>

              <ul
                className={`dropdown-menu ${effectiveTheme === 'dark'? 'dropdown-menu-dark': 'dropdown-menu-light'} p-2`}>
                <li>
                  <button
                    className="dropdown-item p-1"
                    onClick={() =>
                      editor
                        .chain()
                        .focus()
                        .insertTable({
                          rows: 3,
                          cols: 3,
                          withHeaderRow: true,
                        })
                        .run()
                    }
                  >
                    {t('wordEditor.tables.add')}
                  </button>
                </li>

                <li>
                  <button
                    className="dropdown-item p-1"
                    disabled={!editor.isActive('table')}
                    onClick={() => editor.chain().focus().addRowAfter().run()}
                  >
                    {t('wordEditor.tables.addRowAfter')}
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item p-1"
                    disabled={!editor.isActive('table')}
                    onClick={() => editor.chain().focus().addRowBefore().run()}
                  >
                    {t('wordEditor.tables.addRowBefore')}
                  </button>
                </li>

                <li>
                  <button
                    className="dropdown-item p-1"
                    disabled={!editor.isActive('table')}
                    onClick={() => editor.chain().focus().addColumnAfter().run()}
                  >
                    {t('wordEditor.tables.addColumnAfter')}
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item p-1"
                    disabled={!editor.isActive('table')}
                    onClick={() => editor.chain().focus().addColumnBefore().run()}
                  >
                    {t('wordEditor.tables.addColumnBefore')}
                  </button>
                </li>

                <li>
                  <button
                    className="dropdown-item p-1"
                    disabled={!editor.isActive('table')}
                    onClick={() => editor.chain().focus().deleteRow().run()}
                  >
                    {t('wordEditor.tables.deleteRow')}
                  </button>
                </li>

                <li>
                  <button
                    className="dropdown-item p-1"
                    disabled={!editor.isActive('table')}
                    onClick={() => editor.chain().focus().deleteColumn().run()}
                  >
                    {t('wordEditor.tables.deleteColumn')}
                  </button>
                </li>

                <li>
                  <button
                    className="dropdown-item text-danger p-1"
                    disabled={!editor.isActive('table')}
                    onClick={() => editor.chain().focus().deleteTable().run()}
                  >
                    {t('wordEditor.tables.delete')}
                  </button>
                </li>
              </ul>
            </div>
            <EffectThemeButton
              onClick={handleClickUploadImage}
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              title={t('wordEditor.image')}
              disabled={!editor}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faImage}
                  size="1x"
                />
              }
            />
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }} 
            />
            <EffectThemeButton
              onClick={() => editor?.chain().focus().toggleTaskList().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleTaskList()
                  .run()
              }
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              title={t('wordEditor.tasks')}
              style={{height:buttonHeight,width: buttonWidth}}
              active={editor.isActive('taskList')}
              children={<FontAwesomeIcon
                icon={faCheckSquare}
                size="1x"
              />}
            />
          
            <EffectThemeButton
              onClick={() => editor.chain().focus().toggleCode().run()}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .toggleCode()
                  .run()
              }
              className={`btn-outline-secondary rounded p-1 m-0 me-2 mb-1`}
              active={editor.isActive('code')}
              title={t('wordEditor.code')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faCode}
                  size="1x"
                />
              }
            />
            <EffectThemeButton 
              onClick={() => editor.chain().focus().unsetAllMarks().run()}
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              title={t('wordEditor.clearMarks')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faMinusCircle}
                  size="1x"
                />
              }
            />
            <EffectThemeButton 
              onClick={() => editor.chain().focus().clearNodes().run()}
              className={`btn-outline-secondary rounded p-1 m-0 me-2 mb-1`}
              title={t('wordEditor.clearNodes')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faBan}
                  size="1x"
                />
              }
            />
            
            {/* <EffectThemeButton
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              className=''
              style={{height:buttonHeight,width: buttonWidth}}
              active={editor.isActive('codeBlock') }
            >
              Code block
            </button>
            <EffectThemeButton
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className=''
              style={{height:buttonHeight,width: buttonWidth}}
              active={editor.isActive('blockquote')}
            >
              Blockquote
            </button> */}

            <EffectThemeButton 
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              title={t('wordEditor.ruleHorizontal')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                  <FontAwesomeIcon
                  icon={faMinus}
                  size="1x"
                />
              }
            />
            <EffectThemeButton 
              onClick={() => editor.chain().focus().setHardBreak().run()}
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              title={t('wordEditor.hardBreak')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faChainBroken}
                  size="1x"
                />
              }
            />
            <EffectThemeButton
              onClick={() => editor.chain().focus().setParagraph().run()}
              className={`btn-outline-secondary rounded p-1 m-0 me-1 mb-1`}
              // active={editor.isActive('paragraph')}
              title={t('wordEditor.paragraph')}
              style={{height:buttonHeight,width: buttonWidth}}
              children={
                <FontAwesomeIcon
                  icon={faParagraph}
                  size="1x"
                />
              }
            />
          </div>
        </div>
        <div className='col-auto'>
          <div className="row">
            <EffectThemeButton
              onClick={() => editor.chain().focus().undo().run()}
              className='btn-outline-secondary rounded p-1 m-0 me-1 mb-1'
              style={{height:buttonHeight,width: buttonWidth}}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .undo()
                  .run()
              }
              title={t('wordEditor.undo')}
              children={
                <FontAwesomeIcon
                icon={faUndo}
                size="1x"
              />
              }
            />
            <EffectThemeButton
              onClick={() => editor.chain().focus().redo().run()}
              className='btn-outline-secondary rounded p-1 m-0 me-1 mb-1'
              style={{height:buttonHeight,width: buttonWidth}}
              disabled={
                !editor.can()
                  .chain()
                  .focus()
                  .redo()
                  .run()
              }
              title={t('wordEditor.redo')}
              children={
                <FontAwesomeIcon
                  icon={faRotateRight}
                  size="1x"
                />
              }
            />
            <div className="dropdown m-0 p-0" style={{width: buttonWidth}}>
              <EffectThemeButton 
                className="btn-outline-secondary rounded p-1 m-0 me-1 mb-1" 
                type="button" 
                id="dropdownFileDownload"
                data-bs-toggle="dropdown"
                aria-haspopup="true" 
                style={{height:buttonHeight,width: buttonWidth}}
                title={t('wordEditor.export')}
                children={
                  <FontAwesomeIcon
                    icon={faDownload}
                    size="lg"
                  />
                }
              />
              <ul className={`dropdown-menu ${effectiveTheme === 'dark' ? 'dropdown-menu-dark' : 'dropdown-menu-light'} ms-4 p-2 border rounded`} aria-labelledby="dropdownFileDownload" style={{textAlignLast:'start'}}>
                <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1`} onClick={e => setFileFormat('docx')}>
                  Docx
                </li>
                <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1`} onClick={e => setFileFormat('html')}>
                  HTML
                </li>
                <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1`} onClick={e => setFileFormat('json')}>
                  JSON
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const extensionsRichTextEditor = [
  Image,
  TextStyle,
  Highlight,
  Underline,
  FontFamily,
  FontSize,
  Subscript,
  Superscript,

  Table.configure({
    resizable: true,
  }),
  TableRow,
  TableHeader,
  TableCell,

  Color.configure({
    types: ['textStyle', 'listItem', 'heading', 'paragraph'],
  }),

  TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),

  TaskList,
  TaskItem.configure({
    nested: true,
  }),

  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
  }),
];

const content = `
<h2>
  Hi there,
</h2>
<p>
  this is a <em>basic</em> example of <strong>Tiptap</strong>. Sure, there are all kind of basic text styles you’d probably expect from a text editor. But wait until you see the lists:
</p>
<ul>
  <li>
    That’s a bullet list with one …
  </li>
  <li>
    … or two list items.
  </li>
</ul>
<p>
  Isn’t that great? And all of that is editable. But wait, there’s more. Let’s try a code block:
</p>
<pre><code class="language-css">body {
  display: none;
}</code></pre>
<p>
  I know, I know, this is impressive. It’s only the tip of the iceberg though. Give it a try and click a little bit around. Don’t forget to check the other examples too.
</p>
<blockquote>
  Wow, that’s amazing. Good work, boy! 👏
  <br />
  — Mom
</blockquote>
`

export default () => {
  return (
    <EditorProvider slotBefore={<MenuBarRichTextEditor editorIN={null}/>} extensions={extensionsRichTextEditor} content={content}>
    </EditorProvider>
  )
}